import {
  Component,
  ElementRef,
  Input,
  OnInit,
  signal,
  ViewChild,
  Output,
  EventEmitter,
  CUSTOM_ELEMENTS_SCHEMA,
  AfterViewInit,
} from '@angular/core';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { v4 as uuidV4 } from 'uuid';
import {
  CHAT_HISTORY_KEY,
  chatHistory,
  ChatMessage,
  CURRENT_CHAT_SESSION_ID,
  MessageFunctionType,
  MessageType,
  SenderType,
  userType,
  EntityType,
} from './digital-assistant.model';
import { ENTITY_BY_PROMPT_MAP, getDefaultPromptsByEntity, getInitials } from './digital-assistant.helper';
import { DigitalAssistantService } from './digital-assistant.service';
import { ChatResponseMapperService } from './components/chat/services/chat-response-mapper.service';
import { ChatConversationHistoryService } from './components/chat/services/chat-conversation-history.service';
import { CommonModule } from '@angular/common';
import { DigitalAssistantSharedModule } from './digital-assistant-shared.module';
import { TranslationModule } from '@miq/utils';
import { SpinnerLoadingIndicatorComponent } from '@cbre/components';
import { of } from 'rxjs';
import { delay } from 'rxjs/operators';

// Security utility: escape regex metacharacters in dynamic pattern segments
const escapeRegexSpecialChars = (str: string): string => str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

@UntilDestroy()
@Component({
  selector: 'miq-digital-assistant',
  templateUrl: './digital-assistant.component.html',
  styleUrls: ['./digital-assistant.component.scss'],
  imports: [CommonModule, TranslationModule, SpinnerLoadingIndicatorComponent, DigitalAssistantSharedModule],
  providers: [DigitalAssistantService, ChatResponseMapperService, ChatConversationHistoryService],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class DigitalAssistantComponent implements OnInit, AfterViewInit {
  @ViewChild('chatBody', { static: false }) chatBody!: ElementRef;
  resizeObserver!: ResizeObserver;
  @Input() userInfo: Record<string, any> = {};
  @Output() emitCloseChat = new EventEmitter<void>();
  @Output() emitToggleResize = new EventEmitter<void>();
  isExpanded = signal(true);
  isPromptWindowExpanded = signal(true);
  messages = signal<Record<string, any>[]>([]);
  sessionId = signal<string>(null);

  isHistoryLoading = signal(false);
  isResponseInProgress = signal(false);
  sessionIndex = 0;

  public chatInputValue = '';
  public chatHistory: chatHistory[] = [];
  public defaultPrompts: string[] = [];
  public defaultGroupByPrompts: Record<string, string[]> = {};

  constructor(
    private elRef: ElementRef,
    public chatBotService: DigitalAssistantService,
    private chatResponseMapper: ChatResponseMapperService,
    public conversationHistoryService: ChatConversationHistoryService
  ) {}

  ngOnInit(): void {
    this.isHistoryLoading.set(true);
    setTimeout(() => {
      this.defaultPrompts = getDefaultPromptsByEntity(EntityType.Lease); //For now we are showing only lease prompts
      this.defaultGroupByPrompts = ENTITY_BY_PROMPT_MAP[EntityType.Lease];
      this.chatHistory = JSON.parse(localStorage.getItem(CHAT_HISTORY_KEY) || '[]');
      const storedSessionId = localStorage.getItem(CURRENT_CHAT_SESSION_ID);
      if (storedSessionId) {
        this.sessionId.set(storedSessionId);
        this.loadMessages();
      } else {
        this.isHistoryLoading.set(false);
      }
    }, 0);
  }

  ngAfterViewInit() {
    this.resizeObserver = new ResizeObserver(entries => {
      for (const entry of entries) {
        const width = entry.contentRect.width;
        this.isExpanded.set(width < 400);
      }
    });
    this.resizeObserver.observe(this.elRef.nativeElement);
  }

  startNewSession(): void {
    this.chatInputValue = '';
    this.chatBotService.isPredefinedPromptSelected.set(false);
    this.chatBotService.selectedPredefinedPrompt.set('');
    this.sessionId.set(null);
    this.messages.set([]);
    this.isHistoryLoading.set(false);
    localStorage.removeItem(CURRENT_CHAT_SESSION_ID);
    this.chatBotService.trackUserAction('New Chatbot session started');
  }

  // Load messages for the current session
  loadMessages(): void {
    const allSessions = JSON.parse(localStorage.getItem(CHAT_HISTORY_KEY) || '[]') as chatHistory[];
    const currentSession = allSessions.find(session => session.sessionId === this.sessionId());

    this.messages.set(currentSession?.messages || []);
    this.chatHistory = allSessions;
    this.scrollToBottom();
    // Hide spinner after loading messages
    this.isHistoryLoading.set(false);
  }

  // Save messages for the current session
  saveMessages(): void {
    if (this.messages().length === 0) {
      return;
    }

    const allSessions = JSON.parse(localStorage.getItem(CHAT_HISTORY_KEY) || '[]') as chatHistory[];
    this.sessionIndex = allSessions.findIndex((session: chatHistory) => session.sessionId === this.sessionId());

    const sessionData = {
      conversation_history: this.sessionIndex !== -1 ? allSessions[this.sessionIndex]?.conversation_history : [],
      sessionId: this.sessionId(),
      messages: this.messages(),
    };

    if (this.sessionIndex !== -1) {
      // Update existing session
      allSessions[this.sessionIndex] = sessionData;
    } else {
      // Add new session to the beginning of the array
      allSessions.unshift(sessionData);
      if (allSessions.length > 10) {
        allSessions.pop(); // Remove the oldest session (last element)
      }
    }
    this.chatHistory = allSessions;
    localStorage.setItem(CHAT_HISTORY_KEY, JSON.stringify(allSessions));
  }

  toggleWindowSize(): void {
    this.isExpanded.update(prev => !prev);
    this.chatBotService.trackUserAction('Chat Window size toggled');
  }

  togglePromptsWindowSize(): void {
    this.isExpanded.update(prev => !prev);
    this.chatBotService.trackUserAction('Prompts Window size toggled');
  }
  // Logic to close the chat window
  closeChat(): void {
    this.saveMessages();
    this.messages.set([]);
    this.sessionId.set(null);
    this.isHistoryLoading.set(false);
    this.isResponseInProgress.set(false);
    localStorage.removeItem(CURRENT_CHAT_SESSION_ID);
    this.emitCloseChat.emit();
  }

  //feedback logic
  updateMessage(feedback: boolean, index: number): void {
    // Store the current scroll position and scroll height to keep the view stable
    const chatBodyElement = this.chatBody.nativeElement;
    const scrollTop = chatBodyElement.scrollTop;
    const scrollHeight = chatBodyElement.scrollHeight;
    const updatedMessages = this.messages().map((message, i) => {
      if (i === index) {
        return { ...message, feedback };
      }
      return message;
    });
    this.messages.set(updatedMessages);
    this.saveMessages();
    // Use requestAnimationFrame to ensure smooth scroll adjustment
    requestAnimationFrame(() => {
      // Calculate how much the height changed
      const newScrollHeight = chatBodyElement.scrollHeight;
      const heightDifference = newScrollHeight - scrollHeight;

      // Adjust scroll position to maintain the same relative view
      chatBodyElement.scrollTop = scrollTop + heightDifference;
    });

    const feedbackType = feedback ? 'positive' : 'negative';
    const feedbackActionsParams: Record<string, any> = {
      feedbackResponse: {
        messageId: updatedMessages[index].id,
        feedback: feedbackType,
      },
    };
    if (!feedback) {
      feedbackActionsParams.feedbackResponse.content = updatedMessages[index].content;
    }
    this.chatBotService.trackUserAction('Feedback given for message', feedbackActionsParams);
  }

  handleUserMessage(message: string): void {
    if (!this.sessionId()) {
      const newSessionId = uuidV4();
      this.sessionId.set(newSessionId);
      localStorage.setItem(CURRENT_CHAT_SESSION_ID, newSessionId);
    }
    const userMessage: ChatMessage = this._createMessage(message, MessageFunctionType.Text, SenderType.User, true);
    const updatedMessages = [...this.messages(), userMessage];
    this.messages.set(updatedMessages);

    // // Scroll to the bottom after adding the user's message
    this.scrollToBottom();
    this.saveMessages();

    // Add a "typing" message
    const typingMessage = this._createMessage('typing...', MessageFunctionType.Loading, SenderType.Bot, false);
    const updatedMessagesWithTyping = [...this.messages(), typingMessage];
    this.messages.set(updatedMessagesWithTyping);
    this.scrollToBottom();
    this.isResponseInProgress.set(true);
    this.chatBotService
      .sendMessage(this._createRequestMessage(message), this.messages)
      .pipe(untilDestroyed(this))
      .subscribe({
        next: response => {
          const messagesWithoutTyping = this.messages().filter(msg => msg.type !== MessageFunctionType.Loading);
          const present_conversation = response?.conversation?.present_conversation;
          if (!present_conversation || !Array.isArray(present_conversation)) {
            this._handleErrorMessage();
            return;
          }

          const graphAndSummaryAgentObject = present_conversation?.find(
            (item: Record<string, any>) => item.graph_and_summary_agent !== undefined
          );
          const graphAndSummaryAgent = graphAndSummaryAgentObject?.graph_and_summary_agent || [];
          if (response != null && graphAndSummaryAgent) {
            this.conversationHistoryService.createConversationHistory(response, this.chatHistory);

            this.isResponseInProgress.set(false);
            const formattedResponses = this.chatResponseMapper.mapResponses(graphAndSummaryAgent);

            const updatedMessagesWithBot = [
              ...messagesWithoutTyping,
              ...formattedResponses.map((response: Record<string, any>) =>
                this._createMessage(response.content, response.type, SenderType.Bot, false)
              ),
            ];
            this.messages.set(updatedMessagesWithBot);
            // Scroll to the bottom after adding the bot's message
            this.scrollToBottom();
            this.saveMessages();
          } else {
            this._handleErrorMessage();
          }
        },
        error: () => {
          this._handleErrorMessage();
        },
      });
  }

  private _handleErrorMessage() {
    this.isResponseInProgress.set(false);
    const messagesWithoutTyping = this.messages().filter(msg => msg.type !== MessageFunctionType.Loading);
    const errorMessage = this._createMessage(
      'Sorry, something went wrong. Please try again.',
      MessageFunctionType.Text,
      SenderType.Bot,
      false
    );
    const updatedMessagesWithError = [...messagesWithoutTyping, errorMessage];
    this.messages.set(updatedMessagesWithError);
    this.scrollToBottom();
    this.saveMessages();
  }

  //to scroll to the bottom of the chat window
  scrollToBottom(): void {
    if (this.chatBody) {
      requestAnimationFrame(() => {
        this.chatBody.nativeElement.scrollTop = this.chatBody.nativeElement.scrollHeight;
      });
    }
  }

  handlePromptClick(prompt: string): void {
    this.chatInputValue = prompt;

    this.chatBotService.isPredefinedPromptSelected.set(true);
    this.chatBotService.selectedPredefinedPrompt.set(prompt);
  }

  onMessageChange(value: string): void {
    if (!this.chatBotService.isPredefinedPromptSelected()) return;

    const selectedPrompt = this.chatBotService.selectedPredefinedPrompt();
    const isSimilar = this.compareWithPlaceholderPrompts(selectedPrompt, value);

    if (isSimilar) {
      if (this.chatInputValue !== value) this.chatInputValue = value;
      if (!this.chatBotService.isPredefinedPromptSelected()) {
        this.chatBotService.isPredefinedPromptSelected.set(true);
      }
    } else {
      if (this.chatInputValue !== selectedPrompt) this.chatInputValue = selectedPrompt;
      this.chatBotService.isPredefinedPromptSelected.set(false);
      this.chatBotService.selectedPredefinedPrompt.set('');
    }
  }

  private compareWithPlaceholderPrompts(text1: string, text2: string): boolean {
    // Security: escape non-placeholder segments before building the regex to
    // prevent ReDoS via crafted text1 values containing regex metacharacters.
    // Placeholders (<tag>) are replaced with (.+); everything else is escaped.
    const pattern = text1
      .split(/<[^>]+>/)
      .map(escapeRegexSpecialChars)
      .join('(.+)');
    // Create a RegExp from the pattern, anchored to match the whole string
    const regex = new RegExp(`^${pattern}$`, 'i');
    return regex.test(text2);
  }

  closeLeftPanel(): void {
    this.chatBotService.isLeftPanelOpen.update(value => !value);
  }

  loadSavedSession(session: chatHistory): void {
    this.isHistoryLoading.set(true);
    of(session)
      .pipe(delay(0), untilDestroyed(this))
      .subscribe(() => {
        this.chatBotService.isLeftPanelOpen.set(false);
        this.sessionId.set(session.sessionId);
        this.messages.set(session.messages);
        localStorage.setItem(CURRENT_CHAT_SESSION_ID, session.sessionId);
        this.scrollToBottom();
        this.isHistoryLoading.set(false);
      });
  }

  private _createMessage(content: string, type: MessageType, sender: userType, isUser: boolean): ChatMessage {
    const senderInitial =
      isUser && this.userInfo && this.userInfo.firstName && this.userInfo.lastName ? getInitials(this.userInfo) : 'AI';
    return {
      id: uuidV4(),
      content,
      type,
      from: sender,
      senderInitial,
      timestamp: new Date(),
    };
  }

  private _createRequestMessage(message: string) {
    return {
      request_id: uuidV4(),
      user_input: message,
      conversation_history: this.chatHistory[this.sessionIndex]?.conversation_history || [],
      user_details: {
        user_id: this.userInfo?.id,
        user_name: `${this.userInfo?.firstName} ${this.userInfo?.lastName}`,
        user_email: this.userInfo?.emailAddress,
      },
    };
  }
}
